"use client";

import { useState } from "react";
import { Box, Grid, VStack, Text } from "@chakra-ui/react";
import { useTranslations } from "next-intl";
import { IndustryRankingCard } from "@/_components/ui/IndustryRankingCard";
import { getScoreColor, getScoreLabelKey } from "../utils/helpers";

export interface OverviewRankingProps {
  overall: number;
  candidateName: string;
  candidateTitle: string;
  aiSummary: string;
  topPercent?: number | null;
  industry?: string | null;
  shareUrl?: string;
  showShare?: boolean;
}

const ScoreRing = ({ score }: { score: number }) => {
  const t = useTranslations("resume_benchmark");
  const color = getScoreColor(score);
  const colorMap: Record<string, string> = {
    primary: "#7A5CFF",
    green: "#22c55e",
    yellow: "#eab308",
    orange: "#f97316",
    red: "#ef4444",
  };
  const stroke = colorMap[color] ?? "#6366f1";
  const size = 168;
  const center = size / 2;
  const r = 63;
  const circ = 2 * Math.PI * r;
  const dash = (score / 100) * circ;

  return (
    <Box position="relative" boxSize="42" mx="auto">
      <svg width={size} height={size} style={{ transform: "rotate(-90deg)" }}>
        <circle
          cx={center}
          cy={center}
          r={r}
          fill="none"
          stroke="rgba(255,255,255,0.06)"
          strokeWidth={10}
        />
        <circle
          cx={center}
          cy={center}
          r={r}
          fill="none"
          stroke={stroke}
          strokeWidth={10}
          strokeDasharray={`${dash} ${circ}`}
          strokeLinecap="round"
          style={{ transition: "stroke-dasharray 1s ease" }}
        />
      </svg>
      <Box
        position="absolute"
        inset={0}
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
      >
        <Text
          fontSize="4xl"
          fontWeight="medium"
          fontFamily="mono"
          color="white"
          lineHeight="1"
        >
          {score}
        </Text>
        <Text
          fontSize="sm"
          color={`${getScoreColor(score)}.300`}
          fontFamily="spaceGrotesk"
        >
          {t(`scoreLabel.${getScoreLabelKey(score)}`)}
        </Text>
      </Box>
    </Box>
  );
};

export function OverviewRanking({
  overall,
  candidateName,
  candidateTitle,
  aiSummary,
  topPercent,
  industry,
  shareUrl,
  showShare = true,
}: OverviewRankingProps) {
  const t = useTranslations("resume_benchmark");
  const [isExpanded, setIsExpanded] = useState(false);
  const isTruncatable = aiSummary.length > 260;
  const summaryText =
    isTruncatable && !isExpanded ? `${aiSummary.slice(0, 260)}…` : aiSummary;

  return (
    <VStack gap={6} align="stretch" mb={20}>
      <Grid
        templateColumns={{ base: "1fr", md: "auto 1fr" }}
        gap={6}
        alignItems="center"
        bg="whiteAlpha.50"
        rounded="2xl"
        border="1px solid"
        borderColor="whiteAlpha.100"
        p={6}
        mb={6}
      >
        <ScoreRing score={overall} />
        <VStack align="flex-start" gap={2}>
          {/* <HStack gap={2}>
            <Badge
              px={3}
              py={1}
              rounded="full"
              fontSize="xs"
              bg={`${getScoreColor(overall)}.900`}
              color={`${getScoreColor(overall)}.300`}
            >
              {t(`scoreLabel.${getScoreLabelKey(overall)}`)}
            </Badge>
          </HStack> */}
          <Text
            fontSize={{ base: "xl", md: "2xl" }}
            fontWeight="semibold"
            color="neutral.100"
            // fontFamily="spaceGrotesk"
          >
            {candidateName}
          </Text>
          <Text fontSize="sm" color="neutral.400">
            {candidateTitle}
          </Text>
          {summaryText ? (
            <Text fontSize="sm" color="neutral.300" mt={2} lineHeight="1.7">
              {summaryText}
              {isTruncatable ? (
                <>
                  {" "}
                  <Text
                    as="button"
                    color="neutral.500"
                    fontWeight="light"
                    cursor="pointer"
                    _hover={{ color: "primary.400" }}
                    onClick={() => setIsExpanded((prev) => !prev)}
                  >
                    {isExpanded ? t("overview.seeLess") : t("overview.seeMore")}
                  </Text>
                </>
              ) : null}
            </Text>
          ) : null}
        </VStack>
      </Grid>

      <IndustryRankingCard
        percentile={topPercent}
        industry={industry}
        shareUrl={shareUrl}
        showShare={showShare}
      />
    </VStack>
  );
}
